import { Container, Graphics } from 'pixi.js'

import { playSfx } from '../audio/sfx'
import type { BoardLayout } from '../config'
import type { LinesTiming } from '../theme'

/**
 * Win-line overlay for the lines mechanic.
 *
 * Draws what the server said won: a frame around each paid cell, connected
 * along the CONTOUR — the short segments live in the gaps between cells and
 * never cross a symbol, so the art stays readable while the line is lit.
 * Nothing is computed here — the positions arrive in the spin response
 * (docs/lines-round-contract.md §3), the client only connects the dots.
 * Composed into PixiSlot above the reels; ways games simply never call
 * `play`.
 *
 * Sequence: every line on its own (cycle), then all of them together in
 * one deduped gold contour. The lines stay on screen after `play` resolves
 * so the player can study the result; `clear` runs on the next spin.
 */

/** How many lines get their own step (and chime) before the combined finale. */
const MAX_CYCLE_LINES = 4

/** What the overlay needs about one paid line. */
export interface LineWinView {
  line: number
  positions: ReadonlyArray<{ reel: number; row: number }>
}

/**
 * Called every time a set of cells lights up, with how long it stays lit.
 * PixiSlot uses it to animate the winning symbols in step with the lines.
 */
export type LineShownHook = (
  positions: ReadonlyArray<{ reel: number; row: number }>,
  durationMs: number,
) => void

/**
 * Colour per line index. Small on purpose: with 100 lines a unique colour
 * per line is noise, a palette that repeats every few lines still tells
 * two overlapping lines apart.
 */
const LINE_COLOURS = [
  0xffd54a, 0x4ade80, 0x60a5fa, 0xf472b6, 0xfb923c, 0xa78bfa, 0x2dd4bf, 0xf87171,
] as const

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms))

export class PixiLines {
  readonly view = new Container()
  private readonly frames = new Graphics()
  private readonly strokes = new Graphics()
  private readonly L: BoardLayout
  private readonly T: LinesTiming
  private readonly onLine: LineShownHook | null
  /** Bumped by clear/destroy so a running sequence stops drawing. */
  private generation = 0

  constructor(L: BoardLayout, T: LinesTiming, onLine?: LineShownHook) {
    this.L = L
    this.T = T
    this.onLine = onLine ?? null
    // Overlay never eats clicks meant for the board.
    this.view.eventMode = 'none'
    this.view.addChild(this.frames, this.strokes)
  }

  clear(): void {
    this.generation++
    this.frames.clear()
    this.strokes.clear()
  }

  /**
   * `speed` divides every timing, `cycle: false` drops the per-line walk
   * and goes straight to the combined finale. Turbo uses both: the win is
   * still SHOWN — lit cells, fire, the lot — just briefly. Skipping the
   * reveal outright would leave the player with a number and no idea which
   * symbols paid it.
   */
  async play(
    lineWins: readonly LineWinView[],
    opts?: { speed?: number; cycle?: boolean },
  ): Promise<void> {
    this.clear()
    if (lineWins.length === 0) return

    const gen = this.generation
    const speed = Math.max(1, opts?.speed ?? 1)
    const cycle = opts?.cycle ?? true
    const lineShowMs = this.T.lineShowMs / speed
    const betweenLinesMs = this.T.betweenLinesMs / speed
    const allLinesMs = this.T.allLinesMs / speed

    // A single winning line needs no cycle: the finale would replay the
    // exact same combination, and showing it twice reads as a stutter.
    // Only a multi-line hit walks through the lines one at a time first,
    // each in its own colour — the classic reveal, at its original pace.
    // Every shown combination chimes one note higher — the classic
    // ascending win run. One file, played at rising rates (see playSfx).
    // The run walks a major scale and WRAPS after the octave instead of
    // clamping: a clamp made every chime past the cap identical, and a
    // long multi-line hit went monotone right when it should feel like it
    // keeps going. Quiet on purpose — the run fires many times per reveal,
    // and at win-level gain it hammers rather than sparkles.
    const SCALE = [0, 2, 4, 5, 7, 9, 11, 12]
    let step = 0
    const chime = (): void => {
      playSfx('win-small', 0.45, 2 ** (SCALE[step++ % SCALE.length]! / 12))
    }

    // On 100 paylines a single hit can pay a dozen lines or more, and walking
    // every one of them chimes a dozen times — it reads as noise, not as a
    // reveal. Only the first few lines get their own step; the finale below
    // still shows all of them at once, so nothing is hidden.
    const walk = cycle ? lineWins.slice(0, MAX_CYCLE_LINES) : lineWins

    if (cycle && walk.length > 1) {
      for (const win of walk) {
        if (gen !== this.generation) return
        this.frames.clear()
        this.strokes.clear()
        this.draw(win)
        chime()
        this.onLine?.(win.positions, lineShowMs)
        await sleep(lineShowMs)
        if (betweenLinesMs > 0) {
          this.frames.clear()
          this.strokes.clear()
          await sleep(betweenLinesMs)
        }
      }
      if (gen !== this.generation) return
    }

    // Then the finale — every line at once. Overlapping lines share cells,
    // so everything is DEDUPED and drawn once in gold: stacked strokes of
    // different colours are what made some frames look thicker than others.
    this.frames.clear()
    this.strokes.clear()
    const all = new Map<string, { reel: number; row: number }>()
    const segments = new Set<string>()
    for (const win of lineWins) {
      for (const p of win.positions) all.set(`${p.reel}:${p.row}`, p)
      for (let i = 1; i < win.positions.length; i++) {
        const a = win.positions[i - 1]
        const b = win.positions[i]
        segments.add(`${a.reel}:${a.row}>${b.reel}:${b.row}`)
      }
    }
    const GOLD = 0xffd54a
    for (const p of all.values()) this.drawFrame(p, GOLD)
    for (const seg of segments) {
      const [a, b] = seg.split('>').map((s) => {
        const [reel, row] = s.split(':').map(Number)
        return { reel, row }
      })
      this.drawConnector(a, b, GOLD)
    }
    // The finale lands on the next note of the run — the highest one. For
    // a single-line win (no cycle) this is also the only chime.
    chime()
    this.onLine?.([...all.values()], allLinesMs)
    await sleep(allLinesMs)
  }

  private centre(p: { reel: number; row: number }): [number, number] {
    const { symbolSize, pitch } = this.L
    return [p.reel * pitch + symbolSize / 2, p.row * pitch + symbolSize / 2]
  }

  /** Half-extent of the drawn frame — connectors start at this border. */
  private get half(): number {
    return this.L.symbolSize / 2 - 3
  }

  private drawFrame(p: { reel: number; row: number }, colour: number): void {
    const [cx, cy] = this.centre(p)
    const half = this.half
    this.frames
      .roundRect(cx - half, cy - half, half * 2, half * 2, 10)
      .stroke({ width: 4, color: colour, alpha: 0.95 })
  }

  /**
   * Connector between two consecutive cells: only the stretch OUTSIDE both
   * frames, so the line follows the contour instead of cutting across the
   * symbols. Same width as the frame — one solid contour, no glow.
   */
  private drawConnector(
    a: { reel: number; row: number },
    b: { reel: number; row: number },
    colour: number,
  ): void {
    const [ax, ay] = this.centre(a)
    const [bx, by] = this.centre(b)
    const dx = bx - ax
    const dy = by - ay
    // Where the centre-to-centre segment leaves a square of half-extent
    // `half`: max-norm distance grows linearly along the segment. The ends
    // reach 2px INTO the frame stroke, so the contour reads as one solid
    // line — a visible gap at every crossing looked chopped.
    const t = (this.half - 2) / Math.max(Math.abs(dx), Math.abs(dy))
    if (t >= 0.5) return // frames touch or overlap — nothing to draw
    this.strokes
      .moveTo(ax + dx * t, ay + dy * t)
      .lineTo(bx - dx * t, by - dy * t)
      .stroke({ width: 4, color: colour, alpha: 0.95, cap: 'butt' })
  }

  private draw(win: LineWinView): void {
    const colour = LINE_COLOURS[win.line % LINE_COLOURS.length]
    for (const p of win.positions) this.drawFrame(p, colour)
    for (let i = 1; i < win.positions.length; i++) {
      this.drawConnector(win.positions[i - 1], win.positions[i], colour)
    }
  }

  destroy(): void {
    this.generation++
    this.view.destroy({ children: true })
  }
}
